import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleLeft, Globe, Zap, Shield, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

export default function AdminConfigPage() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/config`);
      setConfig(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const save = async () => {
    try {
      await axios.patch(`${API_BASE}/admin/config`, config);
      toast.success('Configuration saved');
    } catch (e) { toast.error('Failed to save'); }
  };

  const toggle = (key) => setConfig(c => ({...c, [key]: !c[key]}));

  if (loading) return <div className="p-4 sm:p-6"><Skeleton className="h-8 w-48 mb-4" /><div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div></div>;

  return (
    <div className="p-4 sm:p-6 max-w-[800px]" data-testid="admin-config">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Platform Configuration</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Toggle features and platform mode</p>
        </div>
        <Button onClick={save} className="rounded-full h-8 sm:h-9 px-3 sm:px-4 text-xs sm:text-sm w-full sm:w-auto">
          <Save className="w-4 h-4 mr-1.5" /> Save Changes
        </Button>
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader className="py-3 px-3 sm:px-4">
            <CardTitle className="flex items-center gap-2 text-xs sm:text-sm"><Globe className="w-4 h-4" /> Platform Mode</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium">Community Mode</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Open scientific platform — no pricing</p>
              </div>
              <Button variant={config.mode === 'community' ? 'default' : 'outline'} size="sm"
                onClick={() => setConfig(c => ({...c, mode: 'community', show_pricing: false, show_billing: false}))}
                className="rounded-full text-[10px] sm:text-xs h-7 sm:h-8 flex-shrink-0">Community</Button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium">SaaS Mode</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Full business mode — billing, subscriptions</p>
              </div>
              <Button variant={config.mode === 'saas' ? 'default' : 'outline'} size="sm"
                onClick={() => setConfig(c => ({...c, mode: 'saas', show_pricing: true, show_billing: true}))}
                className="rounded-full text-[10px] sm:text-xs h-7 sm:h-8 flex-shrink-0">SaaS</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 px-3 sm:px-4">
            <CardTitle className="flex items-center gap-2 text-xs sm:text-sm"><ToggleLeft className="w-4 h-4" /> Feature Toggles</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-4 space-y-2">
            {[
              { key: 'show_pricing', label: 'Show Pricing Page', desc: 'Display pricing tiers on public site' },
              { key: 'show_billing', label: 'Enable Billing', desc: 'Enable Stripe checkout and subscriptions' },
              { key: 'show_blog', label: 'Show Blog', desc: 'Display blog on public site' },
              { key: 'show_whitepaper', label: 'Show Whitepaper', desc: 'Link to architecture whitepaper' },
              { key: 'show_team', label: 'Show Team Page', desc: 'Display team profiles on About page' },
              { key: 'show_architecture', label: 'Show Architecture', desc: 'Display architecture page' },
              { key: 'beta_banner', label: 'Beta Banner', desc: 'Show beta announcement on dashboard' },
              { key: 'maintenance_mode', label: 'Maintenance Mode', desc: 'Show maintenance page to all users' },
            ].map(flag => (
              <div key={flag.key} className="flex items-center justify-between gap-3 py-1.5">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium">{flag.label}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">{flag.desc}</p>
                </div>
                <Switch checked={!!config[flag.key]} onCheckedChange={() => toggle(flag.key)} className="flex-shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 px-3 sm:px-4">
            <CardTitle className="flex items-center gap-2 text-xs sm:text-sm"><Zap className="w-4 h-4" /> Beta Banner</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-4 pb-4">
            <Label className="text-[10px] sm:text-xs">Banner Text</Label>
            <Input value={config.beta_text || ''} onChange={e => setConfig(c => ({...c, beta_text: e.target.value}))}
              placeholder="Public Beta — Join the community" className="mt-1 text-xs h-8" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
